import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'
import SessionManager from '../utils/sessionManager'
import Login from '../views/Login.vue'
import Details from '../views/Details.vue'
import Dashboard from '../views/Dashboard.vue'
import User from '../views/User.vue'
import ViewUser from '../views/ViewUser.vue'
import AddUser from '../views/AddUser.vue'
import EditUser from '../views/EditUser.vue'
import Profile from '../views/Profile.vue'
import Activity from '../views/Activity.vue'
import Members from '@/views/Members.vue'
import Clients from '@/views/Clients.vue'
import AddClient from '@/views/AddClient.vue'
import AddBusiness from '@/views/AddBusiness.vue'
import AddTrust from '@/views/AddTrust.vue'
import QA from '@/views/Qa.vue'
import QaDetails from '@/views/QaDetails.vue'
import CompletedQuestions from '@/views/CompletedQuestions.vue'

import InviteMember from '@/views/InviteMember.vue'
import UserDetails from '@/views/UserDetails.vue'
import InviteClient from '@/views/InviteClient.vue'
import ClientDetails from '@/views/ClientDetails.vue'
import MemberDetails from '@/views/MemberDetails.vue'
import InviteDetails from '@/views/InviteDetails.vue'
import Paypast from '@/views/Paypast.vue'
import UserDebug from '@/views/UserDebug.vue'
import LivLifeUsers from '@/views/LivLifeUsers.vue'

const routes = [
  {
    path: '/',
    name: 'Login',
    component: Login,
    meta: { requiresGuest: true }
  },
  {
    path: '/paypast',
    name: 'Paypast',
    component: Paypast,
    meta: { accessibleToAll: true }
  },
  {
    path: '/details',
    name: 'Details',
    component: Details,
    meta: { accessibleToAll: true }
  },
  {
    path: '/details/:uniqueId',
    name: 'DetailsWithId',
    component: Details,
    props: true,
    meta: { accessibleToAll: true }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true }
  },
  {
    path: '/user',
    name: 'User',
    component: User,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
  {
    path: '/view-user/:id',
    name: 'ViewUser',
    component: ViewUser,
    props: true,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
  {
    path: '/add-user',
    name: 'AddUser',
    component: AddUser,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
  {
    path: '/edit-user/:id',
    name: 'EditUser',
    component: EditUser,
    props: true,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { requiresAuth: true }
  },
  {
    path: '/activity',
    name: 'Activity',
    component: Activity,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
  {
    path: '/members',
    name: 'Members',
    component: Members,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
  {
    path: '/clients',
    name: 'Clients',
    component: Clients,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive', 'Employee'] }
  },
  {
    path: '/add-client',
    name: 'AddClient',
    component: AddClient,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive', 'Employee'] }
  },
  {
    path: '/add-business',
    name: 'AddBusiness',
    component: AddBusiness,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive', 'Employee'] }
  },
  {
    path: '/add-trust',
    name: 'AddTrust',
    component: AddTrust,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive', 'Employee'] }
  },
  {
    path: '/qa',
    name: 'Qa',
    component: QA,
    meta: { requiresAuth: true }
  },
  {
    path: '/qa/:id',
    name: 'QaDetails',
    component: QaDetails,
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/completed-questions',
    name: 'CompletedQuestions',
    component: CompletedQuestions,
    meta: { requiresAuth: true }
  },

  {
    path: '/invite-member',
    name: 'InviteMember',
    component: InviteMember,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
  {
    path: '/user-details/:id',
    name: 'UserDetails',
    component: UserDetails,
    props: true,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
  {
    path: '/invite-client',
    name: 'InviteClient',
    component: InviteClient,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
  {
    path: '/client-details/:id',
    name: 'ClientDetails',
    component: ClientDetails,
    props: true,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive', 'Employee'] }
  },
  {
    path: '/member-details/:id',
    name: 'MemberDetails',
    component: MemberDetails,
    props: true,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
  {
    path: '/invite-details/:id',
    name: 'InviteDetails',
    component: InviteDetails,
    props: true,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
  {
    path: '/user-debug',
    name: 'UserDebug',
    component: UserDebug,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
  {
    path: '/livlife-users',
    name: 'LivLifeUsers',
    component: LivLifeUsers,
    meta: { requiresAuth: true, requiresRole: ['Admin', 'Executive'] }
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Global session manager instance
let sessionManager = null

// Initialize session manager when router is created
const initializeSessionManager = () => {
  if (!sessionManager) {
    sessionManager = new SessionManager(store, router)
    // Make session manager globally accessible
    window.sessionManager = sessionManager
  }
}

// Navigation guards
router.beforeEach(async (to, from, next) => {
  // Wait for auth to initialize if it's still loading
  if (store.state.loading) {
    await store.dispatch('initAuth')
  }

  const isAuthenticated = store.getters.isAuthenticated
  const userRole = store.getters.userRole

  // Initialize session manager
  initializeSessionManager()

  // Check if route is accessible to all users (no restrictions)
  if (to.meta.accessibleToAll) {
    return next()
  }

  // Check if route requires guest access (login/register pages)
  if (to.meta.requiresGuest && isAuthenticated) {
    // Stop session tracking for guest pages
    if (sessionManager) {
      sessionManager.stopSessionTracking()
    }
    
    // Redirect QA Agents to QA page, others to dashboard
    if (userRole === 'QA Agent') {
      return next('/qa')
    } else {
      return next('/dashboard')
    }
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !isAuthenticated) {
    // Stop session tracking if user is not authenticated
    if (sessionManager) {
      sessionManager.stopSessionTracking()
    }
    
    // Add a small delay to prevent flash during logout
    if (from.path !== '/') {
      await new Promise(resolve => setTimeout(resolve, 100))
    }
    return next('/')
  }

  // Check if route requires specific role
  if (to.meta.requiresRole && isAuthenticated) {
    const requiredRoles = to.meta.requiresRole
    if (!requiredRoles.includes(userRole)) {
      // Stop session tracking for unauthorized access
      if (sessionManager) {
        sessionManager.stopSessionTracking()
      }
      
      // Redirect QA Agents to QA page, others to dashboard
      if (userRole === 'QA Agent') {
        return next('/qa')
      } else {
        return next('/dashboard')
      }
    }
  }

  // If user is authenticated and accessing a protected route, ensure session tracking is active
  if (isAuthenticated && to.meta.requiresAuth) {
    if (sessionManager && !sessionManager.isSessionActive()) {
      sessionManager.startSessionTracking()
    }
  }

  next()
})

export default router
